import { useState, useEffect } from "react";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import { Users, Plus, Calendar } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const HR_CATEGORIES = [
  { key: "labourers", label: "Labourers" },
  { key: "millers", label: "Millers" },
  { key: "fitters", label: "Fitters" },
  { key: "loader_drivers", label: "Loader Drivers" },
  { key: "plant_supervisor", label: "Plant Supervisors" },
  { key: "technical_head", label: "Technical Head" },
  { key: "helpers", label: "Helpers" },
  { key: "electricians", label: "Electricians" },
];

const ManpowerPage = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [creating, setCreating] = useState(false);
  const [unitFilter, setUnitFilter] = useState("all");
  const initialForm = { unit: "Unit 1", shift: "DAY", date: new Date().toISOString().split("T")[0] };
  HR_CATEGORIES.forEach(c => initialForm[c.key] = 0);
  const [form, setForm] = useState(initialForm);

  const fetchEntries = async () => {
    try {
      const params = unitFilter !== "all" ? `?unit=${unitFilter}` : "";
      const res = await authFetch(`${API}/factory/manpower${params}`);
      const data = await res.json();
      if (data.success) setEntries(data.data || []);
    } catch { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchEntries(); }, [unitFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const body = { ...form };
      HR_CATEGORIES.forEach(c => { body[c.key] = parseInt(body[c.key]) || 0; });
      const res = await authFetch(`${API}/factory/manpower`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Manpower entry saved");
        setShowAdd(false);
        const resetForm = { unit: "Unit 1", shift: "DAY", date: new Date().toISOString().split("T")[0] };
        HR_CATEGORIES.forEach(c => resetForm[c.key] = 0);
        setForm(resetForm);
        fetchEntries();
      } else toast.error(data.detail || "Failed");
    } catch { toast.error("Network error"); }
    finally { setCreating(false); }
  };

  const getTotal = (entry) => {
    return HR_CATEGORIES.reduce((sum, c) => sum + (entry[c.key] || 0), 0);
  };

  return (
    <div className="space-y-6" data-testid="manpower-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Sora']">HR / Manpower</h1>
          <p className="text-emerald-600 text-sm mt-1">Track daily manpower across units</p>
        </div>
        <Button onClick={() => setShowAdd(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-manpower-btn">
          <Plus className="w-4 h-4 mr-2" /> Log Attendance
        </Button>
      </div>

      <div className="flex gap-3">
        <Select value={unitFilter} onValueChange={setUnitFilter}>
          <SelectTrigger className="w-36 bg-[#1C2B2B] border-emerald-900/50 text-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Units</SelectItem>
            <SelectItem value="Unit 1">Unit 1</SelectItem>
            <SelectItem value="Unit 2">Unit 2</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="bg-[#1C2B2B] border-emerald-900/30 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="manpower-table">
            <thead>
              <tr className="border-b border-emerald-900/30">
                <th className="text-left p-3 text-emerald-600 text-xs font-medium">Date</th>
                <th className="text-left p-3 text-emerald-600 text-xs font-medium">Unit</th>
                <th className="text-left p-3 text-emerald-600 text-xs font-medium">Shift</th>
                {HR_CATEGORIES.map(c => (
                  <th key={c.key} className="text-center p-3 text-emerald-600 text-xs font-medium">{c.label}</th>
                ))}
                <th className="text-center p-3 text-emerald-600 text-xs font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={HR_CATEGORIES.length + 4} className="text-center py-8"><div className="w-6 h-6 spinner mx-auto" /></td></tr>
              ) : entries.length === 0 ? (
                <tr><td colSpan={HR_CATEGORIES.length + 4} className="text-center py-8 text-slate-500">No entries yet</td></tr>
              ) : entries.map((entry, i) => (
                <tr key={entry.manpower_id || i} className="border-b border-emerald-900/20 hover:bg-emerald-900/10" data-testid={`manpower-row-${i}`}>
                  <td className="p-3 text-slate-300 text-xs">{entry.date}</td>
                  <td className="p-3"><Badge variant="outline" className="text-[10px] border-emerald-700 text-emerald-400">{entry.unit}</Badge></td>
                  <td className="p-3"><Badge className={`text-[10px] ${entry.shift === "DAY" ? "bg-amber-900/30 text-amber-400" : "bg-blue-900/30 text-blue-400"}`}>{entry.shift}</Badge></td>
                  {HR_CATEGORIES.map(c => (
                    <td key={c.key} className="p-3 text-center text-white font-mono">{entry[c.key] || 0}</td>
                  ))}
                  <td className="p-3 text-center text-emerald-400 font-mono font-bold">{getTotal(entry)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-lg bg-[#1A2332] border-emerald-900/50 text-white">
          <DialogHeader>
            <DialogTitle className="text-white font-['Sora']">Log Manpower</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 mt-2">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-emerald-600 font-medium">Date</label>
                <Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} className="bg-[#1C2B2B] border-emerald-900/50 text-white mt-1" data-testid="mp-date" />
              </div>
              <div>
                <label className="text-xs text-emerald-600 font-medium">Unit</label>
                <Select value={form.unit} onValueChange={(v) => setForm({...form, unit: v})}>
                  <SelectTrigger className="bg-[#1C2B2B] border-emerald-900/50 text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Unit 1">Unit 1</SelectItem><SelectItem value="Unit 2">Unit 2</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-emerald-600 font-medium">Shift</label>
                <Select value={form.shift} onValueChange={(v) => setForm({...form, shift: v})}>
                  <SelectTrigger className="bg-[#1C2B2B] border-emerald-900/50 text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="DAY">Day</SelectItem><SelectItem value="NIGHT">Night</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {HR_CATEGORIES.map(c => (
                <div key={c.key}>
                  <label className="text-xs text-emerald-600 font-medium">{c.label}</label>
                  <Input type="number" min="0" value={form[c.key]} onChange={(e) => setForm({...form, [c.key]: e.target.value})} className="bg-[#1C2B2B] border-emerald-900/50 text-white mt-1" data-testid={`mp-${c.key}`} />
                </div>
              ))}
            </div>
            <Button type="submit" disabled={creating} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="submit-manpower-btn">
              {creating ? "Saving..." : "Save Entry"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManpowerPage;
